/**
 * WorkflowRegistry — CRUD + in-memory storage for workflow definitions
 *
 * Workflows are held in memory for the session duration.
 * GraphQL persistence can be added when the workflow engine matures.
 *
 * NOTE: No localStorage — all data is session-scoped.
 */

import type { EngineWorkflow, AuditEntityType } from './types';
import { triggerMatcher } from './triggerMatcher';

class WorkflowRegistry {
  private workflows: Map<string, EngineWorkflow> = new Map();
  private loaded = false;

  /**
   * Mark registry as loaded (in-memory only, no external source)
   */
  load(): void {
    if (this.loaded) return;
    this.loaded = true;
    // In-memory only — workflows are registered programmatically via add()
  }

  /**
   * Persist workflows (no-op — in-memory only)
   */
  private persist(): void {
    // In-memory only — no localStorage persistence
  }

  /**
   * Add or update a workflow
   */
  add(workflow: EngineWorkflow): void {
    const now = new Date().toISOString();
    const existing = this.workflows.get(workflow.id);

    const wf: EngineWorkflow = {
      ...workflow,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };

    // Unregister old version from trigger matcher
    if (existing) {
      triggerMatcher.unregister(existing.id);
    }

    this.workflows.set(wf.id, wf);

    if (wf.enabled) {
      triggerMatcher.register(wf);
    }

    this.persist();
  }

  /**
   * Remove a workflow
   */
  remove(id: string): boolean {
    const existed = this.workflows.delete(id);
    if (existed) {
      triggerMatcher.unregister(id);
      this.persist();
    }
    return existed;
  }

  /**
   * Get a workflow by ID
   */
  get(id: string): EngineWorkflow | undefined {
    return this.workflows.get(id);
  }

  /**
   * Get all workflows
   */
  getAll(): EngineWorkflow[] {
    return Array.from(this.workflows.values());
  }

  /**
   * Get workflows filtered by entity type
   */
  getByEntityType(entityType: AuditEntityType): EngineWorkflow[] {
    return Array.from(this.workflows.values()).filter(
      (wf) => wf.entityType === entityType || wf.trigger.entityTypes?.includes(entityType)
    );
  }

  /**
   * Get workflows filtered by tag
   */
  getByTag(tag: string): EngineWorkflow[] {
    return Array.from(this.workflows.values()).filter((wf) => wf.tags.includes(tag));
  }

  /**
   * Enable a workflow
   */
  enable(id: string): boolean {
    const wf = this.workflows.get(id);
    if (!wf) return false;

    wf.enabled = true;
    wf.updatedAt = new Date().toISOString();
    triggerMatcher.register(wf);
    this.persist();
    return true;
  }

  /**
   * Disable a workflow
   */
  disable(id: string): boolean {
    const wf = this.workflows.get(id);
    if (!wf) return false;

    wf.enabled = false;
    wf.updatedAt = new Date().toISOString();
    triggerMatcher.unregister(id);
    this.persist();
    return true;
  }

  /**
   * Get the count of registered workflows
   */
  count(): number {
    return this.workflows.size;
  }

  /**
   * Clear all workflows
   */
  clear(): void {
    for (const id of this.workflows.keys()) {
      triggerMatcher.unregister(id);
    }
    this.workflows.clear();
    this.persist();
  }
}

/**
 * Singleton WorkflowRegistry instance
 */
export const workflowRegistry = new WorkflowRegistry();
